import React, {useEffect, useState} from 'react';
import {Button, Container, Form} from 'react-bootstrap';

import HomeFooter from '../components/HomeFooter';
import HomeReturnLink from '../components/HomeReturnLink';
import XenkiaAWSconfig from '../config';
import '../styling/contact.css';

const initialForm = {name: '', email: '', subject: '', message: '', website: ''};

export default function Contact() {
    const [form, setForm] = useState(initialForm);
    const [submitting, setSubmitting] = useState(false);
    const [notice, setNotice] = useState(null);
    const [noticeVisible, setNoticeVisible] = useState(false);

    useEffect(() => {
        if (!notice) return undefined;

        setNoticeVisible(true);
        const fadeTimer = window.setTimeout(() => setNoticeVisible(false), 4700);
        const removeTimer = window.setTimeout(() => setNotice(null), 5000);

        return () => {
            window.clearTimeout(fadeTimer);
            window.clearTimeout(removeTimer);
        };
    }, [notice]);

    const updateField = event => {
        const {name, value} = event.target;
        setForm(current => ({...current, [name]: value}));
        setNotice(null);
    };

    // Submit contact form handler
    const submitContactForm = async event => {
        event.preventDefault();
        if (submitting) return;

        setSubmitting(true);
        setNotice(null);

        try {
            const response = await fetch(XenkiaAWSconfig.contact.apiUrl, {
                method: 'POST',
                headers: {'Content-Type': 'application/json'},
                body: JSON.stringify({
                    action: 'contact',
                    name: form.name,
                    contactEmail: form.email,
                    subject: form.subject,
                    message: form.message,
                    website: form.website
                })
            });
            const result = await response.json();
            const responseBody = typeof result.body === 'string'
                ? JSON.parse(result.body)
                : (result.body || result);

            if (!response.ok || responseBody.status !== 'Success') {
                throw new Error('The message could not be sent. Please try again or email us directly.');
            }

            setForm(initialForm);
            setNotice({type: 'success', text: 'Thanks for reaching out. Your message has been sent.'});
        } catch (error) {
            setNotice({
                type: 'error',
                text: error instanceof TypeError
                    ? 'We could not connect. Please try again or email us directly.'
                    : (error.message || 'The message could not be sent. Please try again.')
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <>
            <main className="contact-page">
                <section className="contact-section" aria-labelledby="contact-title">
                    <Container className="contact-container">
                        <div className="contact-home-link"><HomeReturnLink /></div>
                        <div className="contact-intro">
                            <p className="contact-eyebrow">GET IN TOUCH</p>
                            <h1 id="contact-title">Let's talk</h1>
                            <p>
                                Tell us how we can help with your 3D product experience.
                            </p>
                        </div>

                        <div className="contact-card">
                            <Form onSubmit={submitContactForm}>
                                <Form.Group className="contact-form-honeypot" aria-hidden="true">
                                    <Form.Label htmlFor="contact-website">Website</Form.Label>
                                    <Form.Control
                                        id="contact-website"
                                        name="website"
                                        value={form.website}
                                        onChange={updateField}
                                        tabIndex={-1}
                                        autoComplete="off"
                                    />
                                </Form.Group>

                                <div className="contact-form-row">
                                    <Form.Group controlId="contact-name">
                                        <Form.Label>Your name</Form.Label>
                                        <Form.Control
                                            name="name"
                                            value={form.name}
                                            onChange={updateField}
                                            placeholder="Name"
                                            autoComplete="name"
                                            maxLength={120}
                                            required
                                        />
                                    </Form.Group>
                                    <Form.Group controlId="contact-email">
                                        <Form.Label>Email address</Form.Label>
                                        <Form.Control
                                            type="email"
                                            name="email"
                                            value={form.email}
                                            onChange={updateField}
                                            placeholder="you@company.com"
                                            autoComplete="email"
                                            maxLength={254}
                                            required
                                        />
                                    </Form.Group>
                                </div>

                                <Form.Group controlId="contact-subject" className="contact-subject-group">
                                    <Form.Label>Subject</Form.Label>
                                    <Form.Control
                                        name="subject"
                                        value={form.subject}
                                        onChange={updateField}
                                        placeholder="What would you like to discuss?"
                                        maxLength={180}
                                        required
                                    />
                                </Form.Group>

                                <Form.Group controlId="contact-message" className="contact-message-group">
                                    <Form.Label>Message</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        name="message"
                                        value={form.message}
                                        onChange={updateField}
                                        placeholder="Write your message here..."
                                        rows={6}
                                        maxLength={5000}
                                        required
                                    />
                                    <Form.Text>{form.message.length}/5000 characters</Form.Text>
                                </Form.Group>

                                {notice && (
                                    <div className={`contact-notice contact-notice--${notice.type}${noticeVisible ? '' : ' contact-notice--fading'}`} role="status" aria-live="polite">
                                        <span className="contact-notice-text">{notice.text}</span>
                                        <button className="contact-notice-close" type="button" aria-label="Close message" onClick={() => setNotice(null)}>
                                            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                                                <path d="m6 6 12 12M18 6 6 18" />
                                            </svg>
                                        </button>
                                    </div>
                                )}

                                <div className="contact-submit-row">
                                    <Button type="submit" className="contact-submit" disabled={submitting}>
                                        {submitting ? (
                                            <><span className="contact-submit-spinner" aria-hidden="true" /> Sending...</>
                                        ) : 'Send message'}
                                    </Button>
                                    <p>Or email us at <a href="mailto:info@xenkia.com">info@xenkia.com</a></p>
                                </div>
                            </Form>
                        </div>
                    </Container>
                </section>
            </main>
            <HomeFooter />
        </>
    );
}
